import { Helmet } from 'react-helmet-async';
import { useTenant } from '../../hooks/useTenant';
import { useLanguage } from '../../hooks/useLanguage';
import type { StructuredData } from '../../utils/structuredData';

interface SEOProps {
  /**
   * Page title (will be appended with site name)
   * If not provided, uses config.seo.title
   */
  title?: string;

  /**
   * Page description for meta tag
   * If not provided, uses config.seo.description
   */
  description?: string;

  /**
   * Keywords for the page
   * If not provided, uses config.seo.keywords
   */
  keywords?: string[];

  /**
   * Canonical URL for the page (relative path like "/ventas" or "/")
   * Will be combined with the base domain from environment variable
   * REQUIRED for SEO best practices
   */
  canonicalPath: string;

  /**
   * Open Graph image URL
   * If not provided, uses config.seo.ogImage
   */
  ogImage?: string;

  /**
   * Open Graph type (default: 'website')
   */
  ogType?: 'website' | 'article' | 'product';

  /**
   * Robots meta tag (default: 'index, follow')
   */
  robots?: string;

  /**
   * Disable indexing for this page (sets robots to 'noindex, nofollow')
   */
  noIndex?: boolean;

  /**
   * Structured data (Schema.org JSON-LD)
   * Can be a single object or an array of structured data objects
   */
  structuredData?: StructuredData | StructuredData[];

  /**
   * Article-specific metadata (for blog posts)
   */
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
    tags?: string[];
  };
}

/**
 * SEO Component for managing page metadata and canonical URLs
 *
 * Usage:
 * ```tsx
 * <SEO
 *   title="Propiedades en Venta"
 *   description="Browse our properties for sale"
 *   canonicalPath="/ventas"
 * />
 * ```
 */
export function SEO({
  title,
  description,
  keywords,
  canonicalPath,
  ogImage,
  ogType = 'website',
  robots = 'index, follow',
  noIndex = false,
  structuredData,
  article,
}: SEOProps) {
  const { config } = useTenant();
  const { currentLanguage, availableLanguages } = useLanguage();

  if (!config) {
    return null;
  }

  // Get base URL from environment variable or use default
  // In production, set VITE_BASE_URL to the actual domain (e.g., https://inmobiliariapremium.cl)
  const baseUrl = import.meta.env.VITE_BASE_URL || 'https://example.com';

  // Construct canonical URL
  // Remove trailing slash from baseUrl and leading slash from canonicalPath if present
  const cleanBaseUrl = baseUrl.replace(/\/$/, '');
  const cleanPath = canonicalPath.startsWith('/') ? canonicalPath : `/${canonicalPath}`;
  const canonicalUrl = `${cleanBaseUrl}${cleanPath}`;

  // Use provided values or fall back to config
  const pageTitle = title ? `${title} | ${config.name}` : config.seo.title;
  const pageDescription = description || config.seo.description;
  const pageKeywords = keywords || config.seo.keywords;
  const pageOgImage = ogImage || config.seo.ogImage;
  const robotsContent = noIndex ? 'noindex, nofollow' : robots;

  // Ensure OG image is an absolute URL
  const absoluteOgImage = pageOgImage.startsWith('http')
    ? pageOgImage
    : `${cleanBaseUrl}${pageOgImage}`;

  // Map language codes to locale formats
  const localeMap: Record<string, string> = {
    'es': 'es_ES',
    'en': 'en_US',
    'pt': 'pt_BR',
  };

  const currentLocale = localeMap[currentLanguage] || 'es_ES';

  return (
    <Helmet>
      {/* HTML lang attribute */}
      <html lang={currentLanguage} />

      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords.join(', ')} />
      <meta name="robots" content={robotsContent} />

      {/* Canonical URL - Critical for SEO */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Hreflang Tags for Multi-language Support */}
      {availableLanguages.length > 1 && availableLanguages.map((lang) => (
        <link
          key={lang}
          rel="alternate"
          hrefLang={lang}
          href={`${cleanBaseUrl}${cleanPath}`}
        />
      ))}

      {/* x-default hreflang for language selector */}
      {availableLanguages.length > 1 && (
        <link
          rel="alternate"
          hrefLang="x-default"
          href={`${cleanBaseUrl}${cleanPath}`}
        />
      )}

      {/* Open Graph Tags for Social Media */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={absoluteOgImage} />
      <meta property="og:site_name" content={config.name} />
      <meta property="og:locale" content={currentLocale} />

      {/* Alternate locales for Open Graph */}
      {availableLanguages.filter(lang => lang !== currentLanguage).map((lang) => (
        <meta
          key={lang}
          property="og:locale:alternate"
          content={localeMap[lang] || 'es_ES'}
        />
      ))}

      {/* Article-specific Open Graph Tags */}
      {article && (
        <>
          {article.publishedTime && (
            <meta property="article:published_time" content={article.publishedTime} />
          )}
          {article.modifiedTime && (
            <meta property="article:modified_time" content={article.modifiedTime} />
          )}
          {article.author && (
            <meta property="article:author" content={article.author} />
          )}
          {article.section && (
            <meta property="article:section" content={article.section} />
          )}
          {article.tags && article.tags.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={absoluteOgImage} />

      {/* Structured Data (Schema.org JSON-LD) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(Array.isArray(structuredData) ? structuredData : [structuredData], null, 2)}
        </script>
      )}
    </Helmet>
  );
}
